import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThumbsUp, Users, Copy, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

export default function ServerCard({ server }) {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  const handleCopyIP = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(`${server.ip_address}:${server.port}`);
    toast.success('IP Address copied!');
  };

  return (
    <div
      className="group relative bg-[#0A0A0A] border border-[#222222] hover:border-[#00E055]/50 rounded-lg overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
      onClick={() => navigate(`/servers/${server.slug || server.id}`)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`server-card-${server.id}`}
    >
      <div className="p-5 space-y-4">
        <div>
          <h3 className="text-xl font-bold tracking-tight mb-2" style={{fontFamily: 'Unbounded, sans-serif'}} data-testid={`server-name-${server.id}`}>
            {server.name}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-2">{server.description}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <span 
            className="px-3 py-1 text-[10px] font-black tracking-widest uppercase bg-[#00E055]/10 text-[#00E055] border border-[#00E055]/20 rounded-sm flex items-center gap-1.5 shadow-[0_0_15px_rgba(0,224,85,0.05)] transition-all group-hover:border-[#00E055]/40"
            style={{ fontFamily: 'Unbounded, sans-serif' }}
          >
            <span className="w-1 h-1 rounded-full bg-[#00E055] shadow-[0_0_5px_#00E055]" />
            {server.category}
          </span>
          <span 
            className="px-3 py-1 text-[10px] font-bold tracking-widest uppercase bg-white/5 text-white/40 border border-white/10 rounded-sm transition-all group-hover:border-white/20"
            style={{ fontFamily: 'Unbounded, sans-serif' }}
          >
            {server.version}
          </span>
          {server.is_online && (
            <span 
              className="px-3 py-1 text-[10px] font-black tracking-widest uppercase bg-[#00E055]/10 text-[#00E055] border border-[#00E055]/20 rounded-sm flex items-center gap-1.5 shadow-[0_0_15px_rgba(0,224,85,0.05)]"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
            >
              <span className="w-1 h-1 rounded-full bg-[#00E055] animate-pulse shadow-[0_0_5px_#00E055]" />
              ONLINE
            </span>
          )}
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-border">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5 text-[#00E055]" data-testid={`server-votes-${server.id}`}>
              <ThumbsUp className="w-4 h-4" />
              <span className="font-bold">{server.vote_count}</span>
            </div>
            {server.player_count !== null && (
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Users className="w-4 h-4" />
                <span className="text-sm">{server.player_count}</span>
              </div>
            )}
          </div>

          {isHovered && (
            <Button
              onClick={handleCopyIP}
              size="sm"
              variant="ghost"
              className="gap-2 text-xs"
              data-testid={`copy-ip-button-${server.id}`}
            >
              <Copy className="w-3 h-3" />
              COPY IP
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
